import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  Command,
  CommandGroup,
  CommandItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ais-ui/ui-core';
import { capitalize, cn } from '@ais-ui/utils';
import { usePagination, useTargetsOnMap } from '../TargetsOnMapContext';
import { useLocalization } from '@ais-ui/locale-management';
import { useMapPanelContext } from '../MapPanel/MapPanelContext';
import { useHorizontalScroll } from '../../Reports/Table/useHorizontalScroll';
import {
  PiBoat,
  PiCaretDown,
  PiCaretLeftLight,
  PiCaretRightLight,
  PiCaretUp,
  PiCaretUpDown,
  PiDotsThreeVertical,
  PiDotsThreeVerticalBold,
  PiFileCsv,
  PiX,
} from 'react-icons/pi';
import { useAISMapContext } from '../AISMapContext';
import { AutoCompleteShipInfo, TargetFromTargetList } from '@ais-ui/websockets';
import { CENTERCOMPONENTS } from '../MapPanel/CenterComponents';

interface TargetsOnMapTableProps {
  className?: string;
}

interface SortableTableHeadProps {
  children: React.ReactNode;
  column: string;
}

const SortableTableHead = ({ children, column }: SortableTableHeadProps) => {
  const {
    orderBy,
    setOrderBy,
    orderAscending,
    setOrderAscending,
    onGetTargets,
  } = useTargetsOnMap();
  const [sortAction, setSortAction] = useState(false);

  const isSorted = orderBy === column;

  const handleSort = useCallback(() => {
    if (orderBy === column) {
      setOrderAscending(!orderAscending);
    } else {
      setOrderAscending(true);
    }
    setOrderBy(column);

    setSortAction(true);
  }, [orderBy, column, setOrderAscending, orderAscending, setOrderBy]);

  useEffect(() => {
    if (sortAction) {
      onGetTargets();
      setSortAction(false);
    }
  }, [sortAction, onGetTargets]);

  return (
    <TableHead className={'cursor-pointer select-none'} onClick={handleSort}>
      <div className="flex items-center justify-between">
        <span>{children}</span>
        <span className="mr-2">
          {isSorted ? (
            orderAscending ? (
              <PiCaretUp className="h-4 w-4" />
            ) : (
              <PiCaretDown className="h-4 w-4" />
            )
          ) : (
            <PiCaretUpDown className="h-4 w-4 opacity-50" />
          )}
        </span>
      </div>
    </TableHead>
  );
};

const TargetsOnMapActionDropDown = () => {
  const { onCreateShiplist, isSubmitting, exportTargetsToCSV } =
    useTargetsOnMap();
  const [shiplistName, setShiplistName] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { messages } = useLocalization();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="m-0">
          Actions <ChevronDownIcon className="h-4 w-4 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <Command>
          <CommandGroup heading="Actions">
            <CommandItem onSelect={exportTargetsToCSV} disabled={isSubmitting}>
              <PiFileCsv className="mr-2 h-4 w-4" />
              {messages.targetsOnMapExportCsvText}
            </CommandItem>
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <CommandItem onSelect={() => setIsPopoverOpen(true)}>
                  <PiBoat className="mr-2 h-4 w-4" />
                  {messages.targetsOnMapCreateShiplist}
                </CommandItem>
              </PopoverTrigger>
              <PopoverContent side={'right'}>
                <Input
                  placeholder="name"
                  value={shiplistName}
                  onChange={(e) => setShiplistName(e.target.value)}
                ></Input>
                <div className="w-full flex justify-end">
                  <Button
                    onClick={() => onCreateShiplist(shiplistName)}
                    className="mt-2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? messages.genericSubmitting
                      : messages.genericCreate}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const TargetsOnMapTable = memo(
  ({ className }: TargetsOnMapTableProps) => {
    const { tableExpanded, setTableExpanded, tableHeight, setCenterComponent } =
      useMapPanelContext();
    const { messages } = useLocalization();

    const viewportRef = useRef<HTMLDivElement | null>(null);

    const { setZoomToShip } = useAISMapContext();

    const { handleScroll, showLeftScrollButton, showRightScrollButton } =
      useHorizontalScroll(viewportRef);

    const { onGetTargets, targets, page, setPage, perPage, total, isLoading } =
      useTargetsOnMap();
    const pagination = usePagination(page, total, perPage);

    const onPrevious = useCallback(() => {
      setPage((prev) => Math.max(prev - 1, 1));
    }, [setPage]);

    const onNext = useCallback(() => {
      setPage((prev) => Math.min(prev + 1, Math.ceil(total / perPage)));
    }, [setPage, total, perPage]);

    useEffect(() => {
      onGetTargets();
    }, [page, onGetTargets]);

    const onGoToTarget = useCallback(
      (target: TargetFromTargetList) => {
        // The backend sets the mmsi as the name in some cases, and if we try to search by mmsi name, it'll not be found
        const name = target.name !== target.mmsi ? target.name : '';
        const autoCompleteShip: AutoCompleteShipInfo = {
          name: name,
          mmsi: target.mmsi,
          imo: target.imo,
          callSign: target.callSign,
          type: 'TT_SHIP',
        };
        setZoomToShip(autoCompleteShip);
      },
      [setZoomToShip]
    );

    const onClose = useCallback(() => {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    }, [setCenterComponent]);

    if (!tableExpanded) {
      return null;
    }

    return (
      <Card
        className={cn(
          'relative flex flex-col h-fit w-full transition-all group/data-table pl-1 pr-3 pb-4 pt-1',
          className
        )}
      >
        <CardHeader className="px-7 py-4 pb-4 text-lg font-normal text-muted-foreground">
          <div className="flex justify-between">
            <div className="flex flex-col space-y-2">
              <TargetsOnMapActionDropDown />
              <div className="text-sm text-muted-foreground pl-1">
                {total} rows
              </div>
            </div>
            <div className="flex relative">
              <div className="flex items-end">
                <div className="flex items-center space-x-4">
                  <div className="text-sm">
                    {page} of {pagination.totalPages}
                  </div>
                  <div className="space-x-4">
                    <Button
                      variant="outline"
                      onClick={onPrevious}
                      disabled={page === 1}
                    >
                      {messages.genericPaginationPrevious}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={onNext}
                      disabled={page >= pagination.totalPages}
                    >
                      {messages.genericPaginationNext}
                    </Button>
                  </div>
                </div>
              </div>
              <Button
                className="absolute top-0 right-0 -mt-2 -mr-7 h-4 w-4"
                variant="ghost"
                size="icon"
                onClick={onClose}
              >
                <PiX className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <div className="flex overflow-hidden">
          <Button
            onClick={() => handleScroll(-200)}
            className={cn(
              'p-0 mr-1',
              !showLeftScrollButton && 'opacity-0 pointer-events-none'
            )}
          >
            <PiCaretLeftLight className="h-4 w-4" />
          </Button>
          <ScrollArea
            infiniteScrollRef={viewportRef}
            observeResize={false}
            className="mb-4 mx-2 flex-grow p-0 overflow-auto border rounded-md max-w-full"
          >
            <Table>
              <TableHeader className="sticky top-0 left-0 right-0 bg-background no-border">
                <TableRow>
                  <TableHead className="w-4">#</TableHead>
                  <SortableTableHead column="name">
                    {messages.targetsOnMapHeaderName}
                  </SortableTableHead>
                  <SortableTableHead column="mmsi">
                    {messages.targetsOnMapHeaderMmsi}
                  </SortableTableHead>
                  <SortableTableHead column="imo">
                    {messages.targetsOnMapHeaderImo}
                  </SortableTableHead>
                  <SortableTableHead column="callsign">
                    {messages.targetsOnMapHeaderCallsign}
                  </SortableTableHead>
                  <SortableTableHead column="shiptype">
                    {messages.targetsOnMapHeaderShipType}
                  </SortableTableHead>
                  <SortableTableHead column="type">
                    {messages.targetsOnMapHeaderAisType}
                  </SortableTableHead>
                  <SortableTableHead column="country">
                    {messages.targetsOnMapHeaderCountry}
                  </SortableTableHead>
                  <TableHead>{messages.targetsOnMapHeaderPosition}</TableHead>
                  <SortableTableHead column="sog">
                    {messages.targetsOnMapHeaderSpeed}
                  </SortableTableHead>
                  <SortableTableHead column="cog">
                    {messages.targetsOnMapHeaderCourse}
                  </SortableTableHead>
                  <SortableTableHead column="heading">
                    {messages.targetsOnMapHeaderHeading}
                  </SortableTableHead>
                  <SortableTableHead column="rot">
                    {messages.targetsOnMapHeaderRateOfTurn}
                  </SortableTableHead>
                  <SortableTableHead column="width">
                    {messages.targetsOnMapHeaderWidth}
                  </SortableTableHead>
                  <SortableTableHead column="length">
                    {messages.targetsOnMapHeaderLength}
                  </SortableTableHead>
                  <SortableTableHead column="draught">
                    {messages.targetsOnMapHeaderDraught}
                  </SortableTableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              {isLoading && (
                <TableBody>
                  <TableRow className="border-0">
                    <TableCell colSpan={17}>
                      <div
                        className="flex justify-center items-center"
                        style={{ height: `${tableHeight - 170}px` }}
                      >
                        <LoadingSpinner />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
              {!isLoading && (
                <TableBody>
                  {targets.map((target, index) => (
                    <TableRow
                      key={`${target.mmsi}-${target.name}`}
                      className="odd:bg-gray-50"
                    >
                      <TableCell className="py-1">
                        {(page - 1) * perPage + index + 1}
                      </TableCell>
                      <TableCell className="font-medium py-1">
                        {capitalize(target.name)}
                      </TableCell>
                      <TableCell className="py-1">{target.mmsi}</TableCell>
                      <TableCell className="py-1">{target.imo}</TableCell>
                      <TableCell className="py-1">{target.callSign}</TableCell>
                      <TableCell className="py-1">{target.shipType}</TableCell>
                      <TableCell className="py-1">{target.type}</TableCell>
                      <TableCell className="py-1">{target.country}</TableCell>
                      <TableCell className="py-1">{`${target.lat.toFixed(
                        4
                      )}, ${target.lon.toFixed(4)}`}</TableCell>
                      <TableCell className="py-1">
                        {target.sog && `${target.sog} knots`}
                      </TableCell>
                      <TableCell className="py-1">
                        {target.cog && `${target.cog} °`}
                      </TableCell>
                      <TableCell className="py-1">
                        {target.heading && `${target.heading} °`}
                      </TableCell>
                      <TableCell className="py-1">
                        {target.rot && `${target.rot} °/min`}
                      </TableCell>
                      <TableCell className="py-1">
                        {target.width && `${target.width} meters`}
                      </TableCell>
                      <TableCell className="py-1">
                        {target.length && `${target.length} meters`}
                      </TableCell>
                      <TableCell className="py-1">
                        {target.draught && `${target.draught} meters`}
                      </TableCell>
                      <TableCell className="py-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <PiDotsThreeVerticalBold className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => onGoToTarget(target)}
                            >
                              View target
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </ScrollArea>
          <Button
            onClick={() => handleScroll(200)}
            className={cn(
              'p-0 ml-1',
              !showRightScrollButton && 'opacity-0 pointer-events-none'
            )}
          >
            <PiCaretRightLight className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    );
  }
);

TargetsOnMapTable.displayName = 'TargetsOnMapTable';
